import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Button, Card, CheckboxField, Spinner } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { EmailListInput, PhoneListInput } from '../../components/phase2';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { section, useScopedSettings } from '../sites/api';
import { FieldList, useSites } from './api';

type Channel = 'email' | 'sms' | 'push';
const CHANNELS: Channel[] = ['email', 'sms', 'push'];

/** The settings card the Sites and Groups pages draw for the notifications module (registered in routes.tsx). */
export function NotificationSettingsCard(props: SettingsCardProps) {
  return props.scope === 'sites' ? <ReceptionCard {...props} /> : <HostAlertsCard {...props} />;
}

function ReceptionCard({ scopeId, canEdit }: SettingsCardProps) {
  const { query, save, values } = useScopedSettings(`/sites/${scopeId}/settings`, 'notifications');
  const reception = section<{ emails?: string[]; phones?: string[] }>(values, 'reception');
  const [emails, setEmails] = useState<string[]>([]);
  const [phones, setPhones] = useState<string[]>([]);
  useEffect(() => {
    setEmails(reception.emails ?? []);
    setPhones(reception.phones ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  const changed = JSON.stringify(emails) !== JSON.stringify(reception.emails ?? []) || JSON.stringify(phones) !== JSON.stringify(reception.phones ?? []);
  return (
    <Card
      title={t('ntf.reception.title')}
      description={t('ntf.reception.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate({ reception: { emails, phones } })}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <EmailListInput label={t('ntf.reception.emails')} value={emails} max={10} disabled={!canEdit} onChange={setEmails} />
        <PhoneListInput label={t('ntf.reception.phones')} value={phones} max={10} disabled={!canEdit} onChange={setPhones} />
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}

interface HostAlerts {
  signIn: Channel[];
  signOut: Channel[];
  quickReplies: boolean;
}

function ChannelBoxes({ legend, value, disabled, onChange }: { legend: string; value: Channel[]; disabled: boolean; onChange: (v: Channel[]) => void }) {
  return (
    <fieldset className="space-y-2">
      <legend className="mb-1 text-sm font-medium text-slate-800">{legend}</legend>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {CHANNELS.map((c) => (
          <CheckboxField key={c} label={t(`ntf.channel.${c}`)} checked={value.includes(c)} disabled={disabled} onChange={(e) => onChange(e.target.checked ? [...value, c] : value.filter((x) => x !== c))} />
        ))}
      </div>
    </fieldset>
  );
}

function HostAlertsCard({ scopeId, canEdit }: SettingsCardProps) {
  const { query, save, values } = useScopedSettings(`/groups/${scopeId}/settings`, 'notifications');
  const stored = section<Partial<HostAlerts>>(values, 'hostAlerts');
  const storedAllow = Array.isArray(values?.['fieldAllowList']) ? (values['fieldAllowList'] as string[]) : [];
  const [alerts, setAlerts] = useState<HostAlerts>({ signIn: ['email'], signOut: [], quickReplies: true });
  const [allow, setAllow] = useState<string[]>([]);
  useEffect(() => {
    setAlerts({ signIn: stored.signIn ?? ['email'], signOut: stored.signOut ?? [], quickReplies: stored.quickReplies ?? true });
    setAllow(storedAllow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);
  const sites = useSites();
  const fieldQueries = useQueries({
    queries: (sites.data ?? []).map((s) => ({
      queryKey: ['notifications', 'fields', s.id],
      queryFn: () => api.get(`/fields/sites/${s.id}`, { query: { scope: 'sign_in', status: 'active' }, schema: FieldList }),
    })),
  });
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values) return <Spinner />;
  // Sensitive fields can never appear in a message, so they are not offered.
  const fields = fieldQueries.flatMap((q) => q.data ?? []).filter((f) => !f.sensitive && f.groupIds.includes(scopeId));
  const changed =
    JSON.stringify(alerts) !== JSON.stringify({ signIn: stored.signIn ?? ['email'], signOut: stored.signOut ?? [], quickReplies: stored.quickReplies ?? true }) || JSON.stringify(allow) !== JSON.stringify(storedAllow);
  const errors = fieldErrors(save.error);
  return (
    <Card
      title={t('ntf.hostAlerts.title')}
      description={t('ntf.hostAlerts.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate({ hostAlerts: alerts, fieldAllowList: allow })}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <ChannelBoxes legend={t('ntf.hostAlerts.signIn')} value={alerts.signIn} disabled={!canEdit} onChange={(signIn) => setAlerts({ ...alerts, signIn })} />
        <ChannelBoxes legend={t('ntf.hostAlerts.signOut')} value={alerts.signOut} disabled={!canEdit} onChange={(signOut) => setAlerts({ ...alerts, signOut })} />
        <CheckboxField label={t('ntf.hostAlerts.quickReplies')} hint={t('ntf.hostAlerts.quickRepliesHint')} checked={alerts.quickReplies} disabled={!canEdit} onChange={(e) => setAlerts({ ...alerts, quickReplies: e.target.checked })} />
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('ntf.hostAlerts.allowList')}</legend>
          <p className="text-xs text-slate-600">{t('ntf.hostAlerts.allowListHint')}</p>
          {fields.length === 0 ? <p className="text-sm text-slate-600">{t('ntf.hostAlerts.noFields')}</p> : null}
          <div className="grid gap-2 sm:grid-cols-2">
            {fields.map((f) => (
              <CheckboxField key={f.id} label={f.label} checked={allow.includes(f.id)} disabled={!canEdit} onChange={(e) => setAllow(e.target.checked ? [...allow, f.id] : allow.filter((x) => x !== f.id))} />
            ))}
          </div>
          {errors['fieldAllowList'] ? <p className="text-sm text-red-700">{errors['fieldAllowList']}</p> : null}
        </fieldset>
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}
